import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  ArrowRight,
  CalendarDays,
  ClipboardList,
  Film,
  LineChart,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "RoleBoard — The Operating System for Actors" },
      {
        name: "description",
        content:
          "Track auditions, organize self tapes, and understand your acting career — all in one place. RoleBoard is the calm home for your acting workflow.",
      },
      { property: "og:title", content: "RoleBoard — The Operating System for Actors" },
      {
        property: "og:description",
        content:
          "Track auditions, organize self tapes, and understand your acting career — all in one place.",
      },
      { property: "og:url", content: "https://roleboard.app/" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/" }],
  }),
  component: Landing,
});

function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Soft auto-redirect for logged-in users? Keep landing public — they can click Tapes.
  useEffect(() => {
    /* no-op */
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />
      <Hero />
      <FeatureGrid />
      <InsightsSection />
      <SelfTapeSection />
      <CalendarSection />
      
      <FinalCTA />
      <Footer />
    </div>
  );
}

function LandingHeader() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo className="h-7 w-auto" />
          <span className="font-semibold tracking-tight text-[15px]">
            RoleBoard
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="#features" className="hover:text-foreground transition-colors">Features</a>
          <a href="#insights" className="hover:text-foreground transition-colors">Insights</a>
          <a href="#tapes" className="hover:text-foreground transition-colors">Self Tapes</a>
          <a href="#calendar" className="hover:text-foreground transition-colors">Calendar</a>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <Button asChild size="sm">
              <Link to="/tapes">Open app</Link>
            </Button>
          ) : (
            <>
              <Button asChild size="sm" variant="ghost" className="hidden sm:inline-flex">
                <Link to="/login">Log in</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/login">Get started</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft ambient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 40% at 70% 0%, color-mix(in oklab, var(--primary) 14%, transparent), transparent 70%), radial-gradient(50% 35% at 10% 20%, color-mix(in oklab, var(--primary) 8%, transparent), transparent 70%)",
        }}
      />
      <div className="container max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 backdrop-blur px-3 py-1 text-xs text-muted-foreground">
            <Sparkles className="size-3.5 text-primary" />
            The operating system for actors
          </div>
          <h1 className="mt-5 text-4xl md:text-6xl font-semibold tracking-[-0.02em] leading-[1.05]">
            Your acting career.{" "}
            <span className="text-foreground">Organized.</span>
          </h1>
          <p className="mt-5 text-[17px] md:text-lg text-muted-foreground max-w-xl leading-relaxed">
            Track every audition, organize your self tapes, and see your career
            unfold over time — all in one calm, considered workspace built for
            actors.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="h-11 px-6">
              <Link to="/login">
                Get started <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            Free during beta · Built with actors, for actors
          </p>
        </div>

        <HeroMockup />
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute -inset-6 -z-10 rounded-[2rem] opacity-60 blur-2xl"
        style={{
          background:
            "linear-gradient(135deg, color-mix(in oklab, var(--primary) 20%, transparent), transparent 60%)",
        }}
      />
      <div className="rounded-2xl border border-border bg-card shadow-elegant overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/70 bg-muted/40">
          <span className="size-2.5 rounded-full bg-destructive/60" />
          <span className="size-2.5 rounded-full bg-warning/70" />
          <span className="size-2.5 rounded-full bg-success/70" />
          <span className="ml-3 text-[11px] text-muted-foreground font-mono">
            roleboard.app/auditions
          </span>
        </div>
        <div className="p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-semibold tracking-tight">Auditions</div>
              <div className="text-xs text-muted-foreground">12 this month</div>
            </div>
            <div className="flex gap-1.5">
              <div className="h-7 w-16 rounded-md bg-muted" />
              <div className="h-7 w-7 rounded-md bg-primary/15" />
            </div>
          </div>

          <div className="space-y-2">
            {[
              { p: "The North Wing", r: "Daniel — Lead", d: "May 2", s: "Callback", tone: "callback" },
              { p: "Quiet Coast", r: "Officer Reyes", d: "Apr 28", s: "Booked", tone: "booked" },
              { p: "Untitled HBO Pilot", r: "Marcus", d: "Apr 24", s: "Submitted", tone: "neutral" },
              { p: "Bright Hours", r: "Alex (Co-star)", d: "Apr 19", s: "No response", tone: "muted" },
            ].map((row) => (
              <div
                key={row.p}
                className="flex items-center justify-between rounded-lg border border-border/70 bg-background px-3.5 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{row.p}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {row.r} · {row.d}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-md border ${
                    row.tone === "booked"
                      ? "bg-success/15 text-success-foreground border-success/30 [color:var(--success)]"
                      : row.tone === "callback"
                        ? "bg-primary/10 text-primary border-primary/20"
                        : row.tone === "muted"
                          ? "bg-muted text-muted-foreground border-transparent"
                          : "bg-card text-foreground/80 border-border"
                  }`}
                >
                  {row.s}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            {[
              { l: "Booked", v: "3" },
              { l: "Callbacks", v: "7" },
              { l: "Submitted", v: "42" },
            ].map((s) => (
              <div key={s.l} className="rounded-lg border border-border/70 bg-background p-3">
                <div className="text-[11px] text-muted-foreground">{s.l}</div>
                <div className="text-lg font-semibold tracking-tight">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureGrid() {
  const features = [
    {
      icon: ClipboardList,
      title: "Audition tracking",
      body:
        "Log every audition with project, role, casting director, genre, and status. Filter, search, and never lose track of a callback again.",
    },
    {
      icon: LineChart,
      title: "Career insights",
      body:
        "See the patterns in your work — role types you keep landing, genres you live in, and how your audition momentum shifts over time.",
    },
    {
      icon: Film,
      title: "Self tape hub",
      body:
        "Upload, trim, auto-rename, and export submission-ready zips. Stop renaming files at midnight before a deadline.",
    },
    {
      icon: CalendarDays,
      title: "Calendar",
      body:
        "Upcoming auditions, callbacks, and deadlines on a single elegant timeline. See your acting career, month by month.",
    },
  ];
  return (
    <section id="features" className="py-20 md:py-28 border-t border-border/60">
      <div className="container max-w-6xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Everything you need</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            One workspace for the whole audition.
          </h2>
          <p className="mt-3 text-muted-foreground">
            From the first self-tape to the booking — quietly organized, beautifully presented.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40"
            >
              <div className="size-9 rounded-lg bg-primary/10 text-primary grid place-items-center mb-5">
                <f.icon className="size-4" />
              </div>
              <h3 className="text-[15px] font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsightsSection() {
  return (
    <section id="insights" className="py-20 md:py-28 border-t border-border/60">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Career insights</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            See the patterns in your work.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            RoleBoard quietly studies your audition history and surfaces the
            patterns — the role types you keep landing, the genres that suit you,
            and how your momentum shifts season to season.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              "“You're consistently landing grounded dramatic roles.”",
              "“Casting tends to see you in the 22–30 range.”",
              "“Your callback rate climbs in the second half of the year.”",
            ].map((q) => (
              <li
                key={q}
                className="rounded-lg border border-border bg-card/60 px-4 py-3 text-foreground/85 italic"
              >
                {q}
              </li>
            ))}
          </ul>
        </div>

        <InsightsMockup />
      </div>
    </section>
  );
}

function InsightsMockup() {
  return (
    <div className="rounded-2xl border border-border bg-card shadow-elegant p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-sm font-semibold tracking-tight">Audition momentum</div>
          <div className="text-xs text-muted-foreground">Last 6 months</div>
        </div>
        <div className="text-xs text-muted-foreground">+38%</div>
      </div>
      {/* Sparkline */}
      <div className="h-40 w-full">
        <svg viewBox="0 0 400 160" className="h-full w-full">
          <defs>
            <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,120 C40,110 70,90 110,95 C150,100 180,70 220,60 C260,50 290,80 330,55 C360,38 380,30 400,20 L400,160 L0,160 Z"
            fill="url(#g)"
          />
          <path
            d="M0,120 C40,110 70,90 110,95 C150,100 180,70 220,60 C260,50 290,80 330,55 C360,38 380,30 400,20"
            fill="none"
            stroke="var(--primary)"
            strokeWidth="2"
          />
        </svg>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[
          { l: "Drama", v: "62%" },
          { l: "Comedy", v: "24%" },
          { l: "Genre", v: "14%" },
        ].map((s) => (
          <div key={s.l} className="rounded-lg border border-border/70 bg-background p-3">
            <div className="text-[11px] text-muted-foreground">{s.l}</div>
            <div className="text-base font-semibold tracking-tight">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SelfTapeSection() {
  return (
    <section id="tapes" className="py-20 md:py-28 border-t border-border/60">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="rounded-2xl border border-border bg-card shadow-elegant p-6">
            <div className="text-sm font-semibold tracking-tight mb-1">Export ready</div>
            <div className="text-xs text-muted-foreground mb-5">Upload → Trim → Rename → Export</div>

            <div className="space-y-2">
              <FilenameRow before="IMG_4421.MOV" after="Lastname_Firstname_Slate.mov" />
              <FilenameRow before="trim_final_v3.mp4" after="Lastname_Firstname_Scene1.mov" />
              <FilenameRow before="WhatsApp_video.mov" after="Lastname_Firstname_Scene2.mov" />
            </div>

            <div className="mt-5 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-xs text-foreground/80 break-words">
              📦 audition-northwing-may2.zip · 3 files · ready to send
            </div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Self tape hub</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            Spend less time organizing tapes.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Drop your raw footage in. Trim, auto-rename to casting's convention,
            and export a submission-ready zip in seconds. No more renaming files
            the night before a deadline.
          </p>
        </div>
      </div>
    </section>
  );
}

function FilenameRow({ before, after }: { before: string; after: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/70 bg-background px-3.5 py-2.5 min-w-0">
      <span className="font-mono text-[11px] text-muted-foreground line-through truncate min-w-0 flex-1">
        {before}
      </span>
      <ArrowRight className="size-3.5 text-muted-foreground shrink-0" />
      <span className="font-mono text-[11px] text-foreground truncate min-w-0 flex-1 text-right">{after}</span>
    </div>
  );
}

function CalendarSection() {
  // Build a tiny mock month
  const days = Array.from({ length: 35 }, (_, i) => i - 2);
  const dots: Record<number, string[]> = {
    3: ["primary"],
    7: ["primary", "callback"],
    11: ["booked"],
    14: ["primary"],
    18: ["callback"],
    22: ["primary", "primary"],
    27: ["primary"],
  };
  return (
    <section id="calendar" className="py-20 md:py-28 border-t border-border/60">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Calendar</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            Your acting career, visualized over time.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Auditions, callbacks, and deadlines on one calm timeline. Click any
            day to see what was on, what came back, and what's next.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-primary" /> Scheduled</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-blue-500" /> Callback</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-green-500" /> Booked</span>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-elegant p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold tracking-tight">May 2026</div>
            <div className="text-xs text-muted-foreground">12 auditions</div>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-[10px] text-muted-foreground mb-1.5">
            {["S","M","T","W","T","F","S"].map((d, i) => (
              <div key={i} className="text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {days.map((d) => {
              const inMonth = d > 0 && d <= 31;
              const ds = dots[d] ?? [];
              return (
                <div
                  key={d}
                  className={`aspect-square rounded-md border text-[11px] p-1.5 flex flex-col justify-between ${
                    inMonth
                      ? "border-border bg-background"
                      : "border-transparent bg-muted/20 text-muted-foreground/50"
                  }`}
                >
                  <span>{inMonth ? d : ""}</span>
                  {inMonth && ds.length > 0 && (
                    <div className="flex gap-0.5">
                      {ds.map((c, i) => (
                        <span
                          key={i}
                          className={`size-1.5 rounded-full ${
                            c === "booked"
                              ? "bg-green-500"
                              : c === "callback"
                                ? "bg-blue-500"
                                : "bg-primary"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}


function FinalCTA() {
  return (
    <section className="py-20 md:py-28 border-t border-border/60">
      <div className="container max-w-3xl mx-auto px-5 md:px-8 text-center">
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
          Take control of your acting career.
        </h2>
        <p className="mt-4 text-muted-foreground text-lg">
          Everything — auditions, tapes, insights, calendar — in one place.
        </p>
        <div className="mt-8 flex justify-center">
          <Button asChild size="lg" className="h-11 px-6">
            <Link to="/login">Join beta</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border/60">
      <div className="container max-w-6xl mx-auto px-5 md:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          <BrandLogo className="h-6 w-auto" />
          <span className="text-sm font-medium tracking-tight">RoleBoard</span>
        </div>
        <nav className="flex items-center gap-5 text-xs text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <span>© {new Date().getFullYear()} RoleBoard</span>
        </nav>
      </div>
    </footer>
  );
}
